import { fetchComposer } from '@nf/utils-common/compose-fetch';
import {
	RewardInfo,
	Labels,
	ExpRecord,
	UpdateEventProgress,
	RawResponse,
	RankingInfo,
	VoucherDetailInfo,
	NewVersionInfo,
	VoucherInfo,
	SabaCoinRecord,
	CustomerPrizeInfo,
	SabaCoinEventInfo,
	SabaCoinRewardLevelInfo
} from '@nf/achievement-system-v2/src/types';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';
import { useEffect } from 'react';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { QueryClient } from '@tanstack/query-core';
import { accountService } from '@/services/account.service';
import { UserInfo } from '@nf/types/account';
import { useAccountInfo } from './account';
import { useQueryClient } from '@tanstack/react-query';

export enum RewardUseVoucherEnum {
	filterForStandardBetSlip = 1,
	filterForGalaxyMarsBetSlip = 2,
	filterForNickName = 3,
	filterForNewRfb = 4
}

export const getRewardInfo = (): Promise<RewardInfo> => {
	return new Promise<RewardInfo>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetRewardInfos`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<RewardInfo> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const rewardInfo: RewardInfo = apiResponse.Data;
		resolve(rewardInfo);
	});
}
export const getRewardLabels = (): Promise<Labels> => {
	return new Promise<Labels>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetResourceI18n`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<Labels> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const labels: Labels = apiResponse.Data;
		resolve(labels);
	});
};

export const getSabaCoinEventInfo = async () => {
	const authToken = getAuthToken();
	const apiUrl = `${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetSabaCoinFlowInfo`;

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl))(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}

	const apiResponse: RawResponse<SabaCoinEventInfo> = await response.json();

	if (!apiResponse.Success) {
		throw new Error(`${apiResponse.Code}: ${apiResponse.Message}`, { cause: apiResponse });
	}

	return apiResponse.Data;
};

export const getSabaCoinRewardLevelsInfo = async () => {
	const authToken = getAuthToken();
	const apiUrl = `${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetSabaCoinFlowLevelInfos`;

	const response = await fetchComposer.getWithBearer(cloudUrlParser(apiUrl))(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}

	const apiResponse: RawResponse<Array<SabaCoinRewardLevelInfo>> = await response.json();

	if (!apiResponse.Success) {
		throw new Error(`${apiResponse.Code}: ${apiResponse.Message}`, { cause: apiResponse });
	}

	return apiResponse.Data;
};


export const redeemCashWithSabaCoin = async (thresholdId: number) => {
	const authToken = getAuthToken();
	const apiUrl = `${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/SetSabaCoinFlowExchange`;

	const response = await fetchComposer.postWithBearer(cloudUrlParser(apiUrl), {
		body: JSON.stringify({ TredHoldLevel: thresholdId })
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}

	const apiResponse: RawResponse<null> = await response.json();

	if (!apiResponse.Success) {
		throw new Error(`${apiResponse.Code}: ${apiResponse.Message}`, { cause: apiResponse });
	}

	return apiResponse.Data;
};

export const addTransaction = (): Promise<RawResponse<number>> => {
	return new Promise<RawResponse<number>>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.postWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/AddRewardTransaction`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<number> = await response.json();
		resolve(apiResponse);
	});
}
export const getHistory = (): Promise<ExpRecord[]> => {
	return new Promise<ExpRecord[]>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetRewardHistorys`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<ExpRecord[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const records: ExpRecord[] = apiResponse.Data;
		resolve(records);
	});
}
export const updateEventProgress = (user: UserInfo | null | undefined, queryClient: QueryClient, rewardId: number): Promise<UpdateEventProgress> => {
	return new Promise<UpdateEventProgress>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const payload = { RewardId: rewardId };
		const response = await fetchComposer.postWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/UpdateEventProgress`), { body: JSON.stringify(payload) })(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<UpdateEventProgress> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const updatedProgress: UpdateEventProgress = apiResponse.Data;

		const {
			CurrentExp,
			VipLevel,
			SabaCoin,
		} = updatedProgress;

		const {
			StreamerRewardId,
			EnableRewardNicknameChange: EnableRewardNicknameChange,
			EnableRewardRiskFreeBet: EnableRewardRiskFreeBet,
			EnableRewardNewRiskFreeBet: EnableRewardNewRiskFreeBet,
			IsVipMax,
			CanSignIn,
			LevelUpExp
		} = user?.RewardInfo || {};

		accountService.updateAccountInfo(queryClient, {
			RewardInfo: {
				SabaCoin: SabaCoin,
				CurrentExp,
				IsVipMax: IsVipMax || false,
				VipLevel,
				CanSignIn: CanSignIn || false,
				EnableRewardSystem: true,
				StreamerRewardId: StreamerRewardId || 0,
				LevelUpExp: LevelUpExp || 0,
				EnableRewardRiskFreeBet: EnableRewardRiskFreeBet !== undefined ? EnableRewardRiskFreeBet : true,
				EnableRewardNicknameChange: EnableRewardNicknameChange !== undefined ? EnableRewardNicknameChange : true,
				EnableRewardNewRiskFreeBet: EnableRewardNewRiskFreeBet !== undefined ? EnableRewardNewRiskFreeBet : true
			}
		});

		resolve(updatedProgress);
	});
}
export const getRankingInfo = (): Promise<RankingInfo[]> => {
	return new Promise<RankingInfo[]>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetRankingInfo`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<RankingInfo[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const records: RankingInfo[] = apiResponse.Data;
		resolve(records);
	});
}
export const getVoucherStock = (): Promise<VoucherDetailInfo[]> => {
	return new Promise<VoucherDetailInfo[]>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetVoucherStock`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<VoucherDetailInfo[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const voucherDetails = apiResponse.Data;
		resolve(voucherDetails);
	});
}
export const purchaseVoucher = (user: UserInfo | null | undefined, queryClient: QueryClient, voucherDetailId: number): Promise<void> => {
	return new Promise<void>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const payload = { VoucherDetailId: voucherDetailId };

		const response = await fetchComposer.postWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/PurchaseVoucher`), { body: JSON.stringify(payload) })(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<string> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		getRewardInfo().then(data => {
			const {
				CurrentExp,
				IsVipMax,
				VipLevel,
				SabaCoin,
				CanSignIn,
				VipLevels
			} = data;

			const {
				StreamerRewardId,
				EnableRewardRiskFreeBet: EnableRewardRiskFreeBet,
				EnableRewardNicknameChange: EnableRewardNicknameChange,
				EnableRewardNewRiskFreeBet: EnableRewardNewRiskFreeBet
			} = user?.RewardInfo || {};
			const levelUpExp = VipLevels.find(level => level.VipLevel == VipLevel)?.LevelUpExp;

			accountService.updateAccountInfo(queryClient, {
				RewardInfo: {
					SabaCoin: SabaCoin,
					CurrentExp,
					IsVipMax,
					VipLevel,
					CanSignIn,
					EnableRewardSystem: true,
					StreamerRewardId: StreamerRewardId || 0,
					LevelUpExp: levelUpExp || 0,
					EnableRewardRiskFreeBet: EnableRewardRiskFreeBet !== undefined ? EnableRewardRiskFreeBet : true,
					EnableRewardNicknameChange: EnableRewardNicknameChange !== undefined ? EnableRewardNicknameChange : true,
					EnableRewardNewRiskFreeBet: EnableRewardNewRiskFreeBet !== undefined ? EnableRewardNewRiskFreeBet : true
				}
			});
		});

		resolve();
	});
}
export const getAvailableVouchers = (): Promise<VoucherInfo[]> => {
	return new Promise<VoucherInfo[]>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetAvailableVouchers`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<VoucherInfo[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
export const getVoucherHistory = (isUsedOnly: boolean): Promise<VoucherInfo[]> => {
	return new Promise<VoucherInfo[]>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetVoucherHistory?isUsedOnly=${isUsedOnly}`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<VoucherInfo[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
export const getSabaCoinRecord = (): Promise<SabaCoinRecord[]> => {
	return new Promise<SabaCoinRecord[]>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetSabaCoinRecords`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<SabaCoinRecord[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
export const getWillBeUsedVouchers = (rewardUseVoucherEnum: RewardUseVoucherEnum): Promise<VoucherInfo[]> => {
	return new Promise<VoucherInfo[]>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetWillBeUsedVouchers?rewardUseVoucherEnum=${Number(rewardUseVoucherEnum)}`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<VoucherInfo[]> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
export const receiveWeeklyAward = (user: UserInfo | null | undefined, queryClient: QueryClient): Promise<void> => {
	return new Promise<void>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.postWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/ReceiveWeeklyAward`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<string> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		getRewardInfo().then(data => {
			const {
				CurrentExp,
				IsVipMax,
				VipLevel,
				SabaCoin,
				CanSignIn,
				VipLevels
			} = data;

			const {
				StreamerRewardId,
				EnableRewardRiskFreeBet: EnableRewardRiskFreeBet,
				EnableRewardNicknameChange: EnableRewardNicknameChange,
				EnableRewardNewRiskFreeBet: EnableRewardNewRiskFreeBet
			} = user?.RewardInfo || {};
			const levelUpExp = VipLevels.find(level => level.VipLevel == VipLevel)?.LevelUpExp;

			accountService.updateAccountInfo(queryClient, {
				RewardInfo: {
					SabaCoin: SabaCoin,
					CurrentExp,
					IsVipMax,
					VipLevel,
					CanSignIn,
					EnableRewardSystem: true,
					StreamerRewardId: StreamerRewardId || 0,
					LevelUpExp: levelUpExp || 0,
					EnableRewardRiskFreeBet: EnableRewardRiskFreeBet !== undefined ? EnableRewardRiskFreeBet : true,
					EnableRewardNicknameChange: EnableRewardNicknameChange !== undefined ? EnableRewardNicknameChange : true,
					EnableRewardNewRiskFreeBet: EnableRewardNewRiskFreeBet !== undefined ? EnableRewardNewRiskFreeBet : true
				}
			});
		});

		resolve();
	});
}
export const getNewVersionInfo = (): Promise<NewVersionInfo> => {
	return new Promise<NewVersionInfo>(async (resolve, reject) => {
		const authToken = getAuthToken();
		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/Reward/GetNewVersionInfo`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<NewVersionInfo> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code
			});
		}
		const records: NewVersionInfo = apiResponse.Data;
		resolve(records);
	});
}
export const getCustomerPrizeInfo = (): Promise<CustomerPrizeInfo> => {
	return new Promise<CustomerPrizeInfo>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/api/SingleMatchJackpot/GetCustomerPrizeInfo`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<CustomerPrizeInfo> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
export const isPrizeInfoNoticed = (): Promise<void> => {
	return new Promise<void>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/api/SingleMatchJackpot/IsPrizeInfoNoticed`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<void> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
export const isJackPotPrizeList = (): Promise<boolean> => {
	return new Promise<boolean>(async (resolve, reject) => {
		const authToken = getAuthToken();

		const response = await fetchComposer.getWithBearer(cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/api/SingleMatchJackpot/IsJackPotPrizeList`))(authToken);
		if (!response.ok) {
			reject({
				status: response.status,
				response
			});
		}
		const apiResponse: RawResponse<boolean> = await response.json();
		if (!apiResponse.Success) {
			reject({
				status: apiResponse.Code,
				message: apiResponse.Message
			});
		}

		resolve(apiResponse.Data);
	});
}
const streamerWatchMinutesThreshold = 15;
const continuousWatchTime = 60 * 1000;
const processingTimers: { rewardId: number, timerId: ReturnType<typeof setTimeout> }[] = [];
const increaseTrackingProgress = (user: UserInfo | undefined | null, queryClient: QueryClient, streamerRewardId: number) => {
	const valueKey = 'reward-streaming-time';
	const existingValue = localStorage.getItem(valueKey) || '0';
	const currentValue = parseInt(existingValue);
	if (!currentValue || isNaN(currentValue)) {
		localStorage.setItem(valueKey, '1');
	} else if (currentValue < streamerWatchMinutesThreshold - 1) {
		localStorage.setItem(valueKey, `${currentValue + 1}`);
	} else {
		localStorage.setItem(valueKey, '0');
		updateEventProgress(user, queryClient, streamerRewardId);
	}
	removeExistingTimer(streamerRewardId);
	enqueueTimer(user, queryClient, streamerRewardId);
};
const getIndexOfExistingRewardId = (rewardId: number) => processingTimers.findIndex(timer => timer.rewardId === rewardId);
const enqueueTimer = (user: UserInfo | undefined | null, queryClient: QueryClient, incomingRewardId?: number) => {
	if (!incomingRewardId) {
		return;
	}
	if (getIndexOfExistingRewardId(incomingRewardId) === -1) {
		const trigger = setTimeout(increaseTrackingProgress.bind(null, user, queryClient, incomingRewardId), continuousWatchTime);
		processingTimers.push({
			timerId: trigger,
			rewardId: incomingRewardId
		});
	}
}
const removeExistingTimer = (targetRewardId?: number) => {
	if (!targetRewardId) {
		return;
	}
	const existingTimerIndex = getIndexOfExistingRewardId(targetRewardId);
	if (existingTimerIndex !== -1) {
		const finishedTimer = processingTimers.splice(existingTimerIndex, 1)[0];
		clearTimeout(finishedTimer.timerId);
	}
};
export const useStreamerWatchTimeTracking = (streamerRewardId?: number) => {
	const { user } = useAccountInfo();
	const queryClient = useQueryClient();

	useEffect(() => {
		enqueueTimer(user, queryClient, streamerRewardId);
		return () => {
			removeExistingTimer(streamerRewardId);
		}
	}, []);
}